import Phaser from 'phaser';

const FADE_IN_MS = 120;
const HOLD_MS = 2200;
const FADE_OUT_MS = 260;
const PAD_X = 16;
const PAD_Y = 10;
const VIEWPORT_MARGIN = 10;

function clampPos(
  scene: Phaser.Scene,
  rawX: number,
  rawY: number,
  halfW: number,
  halfH: number,
): { x: number; y: number } {
  const v = scene.cameras.main.worldView;
  return {
    x: Phaser.Math.Clamp(rawX, v.x + halfW + VIEWPORT_MARGIN, v.right - halfW - VIEWPORT_MARGIN),
    y: Phaser.Math.Clamp(rawY, v.y + halfH + VIEWPORT_MARGIN, v.bottom - halfH - VIEWPORT_MARGIN),
  };
}

export interface NpcSpeechBubble {
  readonly expiresAt: number;
  update(x: number, y: number): void;
  destroy(): void;
}

export function showNpcSpeech(
  scene: Phaser.Scene,
  x: number,
  y: number,
  textValue: string,
): NpcSpeechBubble {
  const container = scene.add.container(0, 0).setDepth(100_500);
  const text = scene.add
    .text(0, 0, textValue, {
      fontFamily: 'Baloo 2, sans-serif',
      fontSize: '22px',
      color: '#17606f',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: 260 },
    })
    .setOrigin(0.5);

  const boxW = Math.min(300, Math.max(150, text.width + PAD_X * 2));
  const boxH = text.height + PAD_Y * 2;
  const bg = scene.add.graphics();
  bg.fillStyle(0xffffff, 0.97);
  bg.lineStyle(3, 0x20b8c7, 1);
  bg.fillRoundedRect(-boxW / 2, -boxH / 2, boxW, boxH, 14);
  bg.strokeRoundedRect(-boxW / 2, -boxH / 2, boxW, boxH, 14);
  bg.fillStyle(0xffffff, 0.97);
  bg.fillTriangle(-10, boxH / 2 - 1, 10, boxH / 2 - 1, 0, boxH / 2 + 12);

  container.add([bg, text]);
  const halfW = boxW / 2;
  const halfH = boxH / 2;
  const p = clampPos(scene, x, y, halfW, halfH);
  container.setPosition(p.x, p.y).setAlpha(0).setScale(0.88);

  scene.tweens.add({
    targets: container,
    alpha: 1,
    scale: 1,
    duration: FADE_IN_MS,
    ease: 'Back.easeOut',
  });
  const fadeOut = scene.tweens.add({
    targets: container,
    alpha: 0,
    delay: FADE_IN_MS + HOLD_MS,
    duration: FADE_OUT_MS,
    ease: 'Cubic.easeIn',
    onComplete: () => container.destroy(),
  });

  return {
    expiresAt: scene.time.now + FADE_IN_MS + HOLD_MS + FADE_OUT_MS,
    update(nx: number, ny: number): void {
      if (!container.active) return;
      const next = clampPos(scene, nx, ny, halfW, halfH);
      container.setPosition(next.x, next.y);
    },
    destroy(): void {
      fadeOut.stop();
      container.destroy();
    },
  };
}
